import { StatusCode } from "api/protocols";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { IOrder } from "redux/reducers/models/order";

export type TOpenFutureBookingPricingModalParams = {
  customerId: number;
  customerTypeId?: number;
  cartCustomerTypeId?: number;
};

type TOpenFutureBookingPricingModal = (params: TOpenFutureBookingPricingModalParams) => Promise<boolean>;

type TOrderLineItem = {
  product?: { type?: string };
  product_type?: string;
};

function lineItemProductType(lineItem: TOrderLineItem): string | undefined {
  return lineItem?.product?.type ?? lineItem?.product_type;
}

export function orderIncludesRegularMembershipProduct(order: { line_items?: TOrderLineItem[] }): boolean {
  return (
    order?.line_items?.some(lineItem => {
      const type = lineItemProductType(lineItem);
      return type === "Membership" || type === "Group Membership";
    }) ?? false
  );
}

export function orderIncludesCartMembershipProduct(order: { line_items?: TOrderLineItem[] }): boolean {
  return order?.line_items?.some(lineItem => lineItemProductType(lineItem) === "Membership Add On") ?? false;
}

export function didCartCustomerTypeIdChange(
  previousCartCustomerTypeId: number | null | undefined,
  nextCartCustomerTypeId: number | null | undefined,
): boolean {
  return (previousCartCustomerTypeId ?? null) !== (nextCartCustomerTypeId ?? null);
}

export type TPromptFutureBookingPricingAfterMembershipOrderParams = Pick<IOrder, "customer_id" | "line_items"> & {
  /** Cart customer type on the profile before the order completes; required for cart add-on sales to detect a real change. */
  previousCartCustomerTypeId?: number | null;
  membership_slots?: Array<{ customer_id?: number | null }>;
};

export async function promptFutureBookingPricingAfterMembershipOrder(
  order: TPromptFutureBookingPricingAfterMembershipOrderParams,
  openIfNeeded: TOpenFutureBookingPricingModal,
): Promise<void> {
  const includesRegularMembership = orderIncludesRegularMembershipProduct(order);
  const includesCartMembership = orderIncludesCartMembershipProduct(order);
  const recipientCustomerIds = Array.from(
    new Set(
      (order.membership_slots ?? [])
        .map(slot => slot.customer_id)
        .filter((customerId): customerId is number => Boolean(customerId)),
    ),
  );

  if (
    !includesRegularMembership &&
    !includesCartMembership &&
    recipientCustomerIds.length === 0
  ) {
    return;
  }

  const customerIds =
    recipientCustomerIds.length > 0
      ? recipientCustomerIds
      : order.customer_id
        ? [order.customer_id]
        : [];

  if (customerIds.length === 0) {
    return;
  }

  for (const customerId of customerIds) {
    const customerRes = await GetCustomer({ id: customerId }, false);
    const customer = customerRes.data?.[0];

    if (customerRes.status !== StatusCode.OK) {
      continue;
    }

    const customerTypeId = includesRegularMembership ? customer?.customer_type_id ?? undefined : undefined;
    const cartCustomerTypeId =
      includesCartMembership &&
      didCartCustomerTypeIdChange(order.previousCartCustomerTypeId, customer?.cart_customer_type_id)
        ? customer?.cart_customer_type_id ?? undefined
        : undefined;

    if (!customerTypeId && !cartCustomerTypeId) {
      continue;
    }

    await openIfNeeded({
      customerId,
      customerTypeId,
      cartCustomerTypeId,
    });
  }
}
